import { useEffect, useState } from 'react';
import { invitationsPubliquesApi } from '../../api/resources';
import {
  LONGUEUR_MOT_DE_PASSE,
  adresseApresAcceptation,
  estDuBureau,
  reprocheSaisie,
} from '../../lib/invitation';
import { C } from '../../theme/palette';
import { GuideInstallation } from '../../pwa/GuideInstallation.jsx';
import { EtapeCode } from '../auth/EtapeCode.jsx';
import { NOM_LOGICIEL } from '../../lib/marque';

const ROLES = {
  OUVRIER: 'ouvrier',
  TECHNICIEN: 'chef d’équipe',
  SECRETAIRE: 'secrétariat',
  COMPTABLE: 'comptable',
  PATRON: 'direction',
};

const carte = {
  background: '#fff',
  border: '1px solid ' + C.border,
  borderRadius: 14,
  padding: 18,
  marginBottom: 14,
};

const etiquette = {
  display: 'block',
  fontSize: 11,
  fontWeight: 700,
  color: C.muted,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  marginBottom: 6,
};

const champ = {
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid ' + C.border,
  borderRadius: 10,
  padding: '12px 12px',
  fontSize: 16,
  color: C.ink,
  background: '#fff',
  fontWeight: 600,
  outline: 'none',
};

function Cadre({ children }) {
  return (
    <div style={{ minHeight: '100svh', background: C.bg, color: C.ink, padding: '22px 14px' }}>
      <div style={{ maxWidth: 520, margin: '0 auto' }}>{children}</div>
    </div>
  );
}

function Bouton({ children, onClick, disabled, type = 'button', secondaire }) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        width: '100%',
        background: secondaire ? '#fff' : C.green,
        color: secondaire ? C.navy : '#fff',
        border: secondaire ? '1px solid ' + C.border : 'none',
        borderRadius: 12,
        padding: '14px 16px',
        fontSize: 15,
        fontWeight: 800,
        cursor: disabled ? 'wait' : 'pointer',
        opacity: disabled ? 0.65 : 1,
      }}
    >
      {children}
    </button>
  );
}

/**
 * Page que l'employé ouvre par le lien d'invitation reçu de son patron.
 *
 * <p>Il n'a pas encore d'accès : la page se suffit. Elle lui montre qui
 * l'invite et le laisse choisir son adresse e-mail et son mot de passe — le
 * patron l'a voulu ainsi pour tous, ouvriers compris : plus de code à quatre
 * chiffres à choisir ici. À la validation, son accès est ouvert et sa session
 * aussi (après le code parti à cette adresse, si le serveur le demande) : il
 * entre dans son espace, où son contrat l'attend s'il reste à signer.
 *
 * @param {object} props propriétés du composant
 * @param {string} props.jeton jeton porté par le lien
 * @returns {JSX.Element} la page
 */
export function PageInvitation({ jeton }) {
  const [page, setPage] = useState(null);
  const [refus, setRefus] = useState('');
  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [acces, setAcces] = useState(null);
  // Acceptation faite, session pas encore ouverte : le code est attendu.
  const [enAttente, setEnAttente] = useState(null);

  useEffect(() => {
    let annule = false;
    invitationsPubliquesApi
      .page(jeton)
      .then((p) => {
        if (annule) return;
        setPage(p);
        setEmail(p?.email || '');
      })
      .catch((e) => {
        if (!annule) {
          setRefus(
            e?.status === 404
              ? 'Ce lien d’invitation n’existe pas. Vérifie l’adresse, ou demande un nouveau lien à ton employeur.'
              : e?.message ||
                  'Cette invitation n’est plus valable. Demande un nouveau lien à ton employeur.',
          );
        }
      });
    return () => {
      annule = true;
    };
  }, [jeton]);

  useEffect(() => {
    document.title = page ? `Invitation — ${page.societe}` : 'Invitation';
  }, [page]);

  // Le bureau entre par son adresse et son mot de passe : pas de code à
  // quatre chiffres, qui lui ouvrait offres et factures avec le seul code de
  // la société, connu de tous les ouvriers.
  const bureau = estDuBureau(page?.role);

  const valide = async (e) => {
    e.preventDefault();
    const reproche = reprocheSaisie({ email, motDePasse, bureau: true });
    if (reproche) {
      setErreur(reproche);
      return;
    }
    setErreur('');
    setEnvoi(true);
    try {
      const ouvert = await invitationsPubliquesApi.accepte(jeton, {
        email: email.trim(),
        motDePasse,
      });
      if (ouvert?.code?.codeRequis) {
        setEnAttente(ouvert);
        return;
      }
      setAcces(ouvert);
    } catch (err) {
      setErreur(err?.message || 'L’accès n’a pas pu être ouvert. Réessaie dans un instant.');
    } finally {
      setEnvoi(false);
    }
  };

  if (refus) {
    return (
      <Cadre>
        <div style={{ ...carte, textAlign: 'center', padding: 32 }}>
          <div style={{ fontSize: 34, marginBottom: 10 }}>🔗</div>
          <h1 style={{ fontSize: 18, margin: '0 0 8px' }}>Invitation indisponible</h1>
          <p role="alert" style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.5 }}>
            {refus}
          </p>
        </div>
      </Cadre>
    );
  }

  if (!page) {
    return (
      <Cadre>
        <div style={{ ...carte, textAlign: 'center', color: C.muted, fontSize: 14 }} role="status">
          Chargement…
        </div>
      </Cadre>
    );
  }

  const prenom = page.prenom || page.nom || '';

  if (enAttente && !acces) {
    return (
      <Cadre>
        <header style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 12, color: C.grey, fontWeight: 700 }}>Invitation</div>
          <div style={{ fontSize: 20, fontWeight: 900 }}>{page.societe}</div>
        </header>
        <section style={carte}>
          <EtapeCode etat={enAttente.code} onConnecte={() => setAcces(enAttente)} />
        </section>
      </Cadre>
    );
  }

  if (acces) {
    return (
      <Cadre>
        <div style={{ ...carte, textAlign: 'center' }}>
          <div style={{ fontSize: 34, marginBottom: 8 }}>✅</div>
          <h1 style={{ fontSize: 20, margin: '0 0 6px' }}>Bienvenue, {prenom} !</h1>
          <p style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.5 }}>
            Ton accès à <b>{page.societe}</b> est ouvert. Tu es connecté.
          </p>
        </div>
        {bureau ? (
          <div style={carte}>
            <span style={etiquette}>Pour te reconnecter plus tard</span>
            <div style={{ fontSize: 14, lineHeight: 1.6 }}>
              Ouvre SwissTech et connecte-toi avec ton adresse e-mail et ton mot de passe, sur ton
              téléphone ou un ordinateur.
            </div>
          </div>
        ) : (
          <>
            <GuideInstallation
              nom={NOM_LOGICIEL}
              apres="Elle s’ouvre comme une application : tu te connectes avec ton adresse e-mail et ton mot de passe."
            />
            <div style={carte}>
              <span style={etiquette}>Pour te reconnecter plus tard</span>
              <div style={{ fontSize: 14, lineHeight: 1.6 }}>
                Ouvre SwissTech et connecte-toi avec ton adresse e-mail et ton mot de passe, sur ton
                téléphone ou un ordinateur.
              </div>
            </div>
          </>
        )}
        {acces.contratASigner && (
          <div style={{ ...carte, background: C.amberL, border: '1px solid ' + C.amber }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: C.amber }}>
              📄 Ton contrat de travail t’attend
            </div>
            <div style={{ fontSize: 13, color: C.grey, marginTop: 4, lineHeight: 1.5 }}>
              Relis-le, puis signe-le au doigt dans ton espace : c’est la dernière étape.
            </div>
          </div>
        )}
        <a
          href={adresseApresAcceptation(acces.contratASigner)}
          style={{
            display: 'block',
            textAlign: 'center',
            textDecoration: 'none',
            background: C.green,
            color: '#fff',
            borderRadius: 12,
            padding: '14px 16px',
            fontSize: 15,
            fontWeight: 800,
          }}
        >
          {acces.contratASigner ? 'Continuer → Signer mon contrat' : 'Entrer dans mon espace'}
        </a>
      </Cadre>
    );
  }

  return (
    <Cadre>
      <header style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 12, color: C.grey, fontWeight: 700 }}>Invitation</div>
        <div style={{ fontSize: 20, fontWeight: 900 }}>{page.societe}</div>
      </header>

      <section style={carte}>
        <h1 style={{ fontSize: 18, margin: '0 0 6px' }}>Bonjour {prenom},</h1>
        <p style={{ fontSize: 14, color: C.grey, margin: 0, lineHeight: 1.55 }}>
          {bureau ? (
            <>
              <b style={{ color: C.ink }}>{page.societe}</b> t’invite à rejoindre SwissTech,
              l’application de l’entreprise, comme{' '}
              <b style={{ color: C.ink }}>{ROLES[page.role]}</b>. Choisis ton adresse e-mail et un
              mot de passe : c’est avec eux que tu te connecteras, sur ton téléphone ou un
              ordinateur.
            </>
          ) : (
            <>
              <b style={{ color: C.ink }}>{page.societe}</b> t’invite à rejoindre SwissTech,
              l’application de l’entreprise, comme{' '}
              <b style={{ color: C.ink }}>{ROLES[page.role] || 'ouvrier'}</b>. Choisis ton adresse
              e-mail et un mot de passe : c’est avec eux que tu te connecteras, sur ton téléphone ou
              un ordinateur.
            </>
          )}
        </p>
      </section>

      <form onSubmit={valide} noValidate>
        <section style={carte}>
          <label style={etiquette} htmlFor="email">
            Adresse e-mail
          </label>
          <input
            id="email"
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={champ}
          />
          <label style={{ ...etiquette, marginTop: 12 }} htmlFor="motDePasse">
            Mot de passe ({LONGUEUR_MOT_DE_PASSE} caractères au moins)
          </label>
          <input
            id="motDePasse"
            type="password"
            autoComplete="new-password"
            value={motDePasse}
            onChange={(e) => setMotDePasse(e.target.value)}
            style={champ}
          />
        </section>

        {erreur && (
          <div
            role="alert"
            style={{
              background: C.redL,
              color: C.red,
              border: '1px solid ' + C.red,
              borderRadius: 10,
              padding: '10px 12px',
              fontSize: 13,
              fontWeight: 700,
              marginBottom: 14,
              lineHeight: 1.5,
            }}
          >
            {erreur}
          </div>
        )}

        <Bouton type="submit" disabled={envoi}>
          {envoi ? 'Ouverture de ton accès…' : '✓ Valider et entrer'}
        </Bouton>
        <p style={{ fontSize: 11, color: C.muted, textAlign: 'center', marginTop: 12 }}>
          Ce lien ne sert qu’une fois. Ton mot de passe est enregistré chiffré.
        </p>
      </form>
    </Cadre>
  );
}
